import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { LinearProgress, makeStyles } from '@material-ui/core'
import { Avatar, Tooltip, Typography, useClientOptions } from '@paintscout/ui'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import ProductionEventPopper from '../ProductionEventPopper'
import { getCrews, getQuoteTypeOptions } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { CREW_COLORS } from '@paintscout/util'
import { getProjectTypeLabel } from '../util'

const useStyles = makeStyles<Theme, { color: string }>((theme) => ({
  root: {
    backgroundColor: ({ color }) => `${color}20`,
    borderRadius: theme.borderRadius.md,
    borderLeft: ({ color }) => `4px solid ${color || theme.paintscout.palette.primary}`,
    cursor: 'pointer',
    boxShadow: theme.shadows[1],
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ color }) => `${color}40`,
        transition: 'background-color 0.1s ease-in-out'
      }
    }
  },
  content: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1),
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(1)
    }
  },
  mainInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    overflow: 'hidden'
  },
  footer: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(1.5)
    }
  },
  smallText: {
    fontSize: theme.typography.pxToRem(12)
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  progressRoot: {
    height: theme.typography.pxToRem(6),
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.4)'
  },
  progressBar: {
    backgroundColor: ({ color }) => color,
    borderRadius: '8px',
    transition: 'none'
  },
  avatar: {
    padding: '0px !important'
  },
  crewAvatarIcon: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(10),
    display: 'block',
    lineHeight: 1,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'geometricPrecision'
  }
}))

interface WeekEventProps {
  event: CalendarEvent
}

export function WeekEvent(props: WeekEventProps) {
  const { event } = props

  const { options } = useClientOptions()
  const { canDeactivateSettingsItems } = useFlags()
  const crews = getCrews({ options, includeTrashed: true })
  const eventCrew = event.crew ? crews.find((crew) => crew._id === event.crew) : null
  const classes = useStyles({ color: eventCrew?.color || CREW_COLORS[0][0] })
  const quoteTypes = getQuoteTypeOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const quoteType = projectTypeLabel ? quoteTypes.find((type) => type.value === projectTypeLabel) : null

  const subtitle = [quoteType?.label, `${event.progress || 0}% complete`].filter(Boolean).join(' · ')

  return (
    <>
      <div className={classes.root} onClick={handleClick}>
        <div className={classes.content}>
          <Tooltip content={eventCrew?.name || null} hideIcon>
            <Avatar
              className={classes.avatar}
              disableTooltip
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: eventCrew?.color || CREW_COLORS[0][0],
                color: '#fff',
                transform: 'none'
              }}
            >
              <i className={`fa-light fa-users ${classes.crewAvatarIcon}`} />
            </Avatar>
          </Tooltip>
          <div className={classes.mainInfo}>
            <Typography weight={500} className={classes.ellipsis}>
              {event?.deal?.name}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classNames(classes.smallText, classes.ellipsis)}
            >
              {subtitle}
            </Typography>
          </div>
        </div>
        <div className={classes.footer}>
          <LinearProgress
            classes={{
              root: classes.progressRoot,
              barColorPrimary: classes.progressBar
            }}
            value={event.progress || 0}
            variant="determinate"
          />
        </div>
      </div>

      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
